// Feature: MCRO001-F0028 (inherits MCRO001-F0026/F0012)
// Test: T-MCRO-028 component/accessibility contract
//
// Gutter confinement, hover-edit expansion, optimistic snapped drags and
// comment suppression: Feature MCRO001-F0060 (parented to F0028), Test
// T-MCRO-060 — docs/process/tests/T-MCRO-060-rail-gutter-hover-edit.md,
// choreo MR !148 amends MCRO001-D006 §4.2. Tracking: studioaeye-WebUI #42.
//
// MCRO001-F0085 / T-MCRO-085 (rail line presence, choreo issue #39): a line
// for a served map is never silently dropped. An unanchorable range renders
// as an honest anchor-unresolved line (Case B) with retime/shift withheld —
// no fake bar targets (Case C); a media-degraded candidate keeps its line and
// states what is unavailable (Case A); a served record that failed map
// validation renders an honest error line via `errorCandidates` instead of
// vanishing.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  applyPriorityOrder,
  demonstrationLanes,
  orderedDemonstrations,
} from './demonstrationPriority';
import { mediaTrackColours, mediaTrackKey } from './mediaTrackColour';
import {
  moveTimingMapEndpoint,
  previewShiftedWindow,
  reorderPriority,
  shiftTimingMapWindow,
  timingMapMutationRequest,
} from './demonstrationTimingRailModel';
import { measureCanvasGutterPx, railGeometryStyle } from './demonstrationTimingRailGeometry';

// Owner ruling (2026-08-26): rails fold back into the gutter margin once the
// pointer truly leaves them (and re-enable canvas commenting), but a bare
// pointerleave/blur would make that fold-back twitch on a fast pass between
// adjacent lanes or from an endpoint to the body. This grace window is the
// same order of magnitude as common browser "hover intent" debounces
// (roughly 150-300ms) — long enough to survive that in-rail hand-off,
// short enough that leaving the rail still reads as immediate.
const RAIL_EDIT_MODE_FOLD_GRACE_MS = 220;

const barIndexAtCount = (bars, count, endpoint = 'start') => bars.findIndex((bar) => endpoint === 'end'
  ? bar.endCount === count
  : bar.startCount === count);

function elementForCount(root, count) {
  return [...(root?.querySelectorAll?.('[data-count-start][data-count-end]') || [])]
    .find((element) => count >= Number(element.dataset.countStart)
      && count <= Number(element.dataset.countEnd));
}

function yForCount(root, bars, count, endEdge = false) {
  const rootRect = root?.getBoundingClientRect?.();
  const element = elementForCount(root, count);
  const rect = element?.getBoundingClientRect?.();
  if (rootRect && element && rect && rect.height > 0) {
    const first = Number(element.dataset.countStart);
    const last = Number(element.dataset.countEnd);
    const rows = Math.max(1, Math.round((last - first + 1) / 8));
    const row = Math.max(0, Math.min(rows - 1, Math.floor((count - first) / 8)));
    return rect.top - rootRect.top + ((row + (endEdge ? 1 : 0)) / rows) * rect.height;
  }
  const index = bars.findIndex((bar) => count >= bar.startCount && count <= bar.endCount);
  const usableHeight = Math.max(1, rootRect?.height || root?.scrollHeight || bars.length * 32);
  return ((Math.max(0, index) + (endEdge ? 1 : 0)) / Math.max(1, bars.length)) * usableHeight;
}

function barAtY(root, bars, clientY) {
  const elements = [...(root?.querySelectorAll?.('[data-count-start][data-count-end]') || [])];
  const containing = elements.find((element) => {
    const rect = element.getBoundingClientRect();
    return rect.height > 0 && clientY >= rect.top && clientY <= rect.bottom;
  });
  if (containing) {
    const rect = containing.getBoundingClientRect();
    const first = Number(containing.dataset.countStart);
    const last = Number(containing.dataset.countEnd);
    const rows = Math.max(1, Math.round((last - first + 1) / 8));
    const row = Math.max(0, Math.min(rows - 1, Math.floor(((clientY - rect.top) / rect.height) * rows)));
    return bars.find((bar) => bar.startCount === first + row * 8) || null;
  }
  const rect = root?.getBoundingClientRect?.();
  const ratio = rect?.height > 0 ? (clientY - rect.top) / rect.height : 0;
  return bars[Math.max(0, Math.min(bars.length - 1, Math.floor(ratio * bars.length)))] || null;
}

export default function DemonstrationTimingRail({
  candidates = [],
  errorCandidates = [],
  revisionId,
  sections = [],
  authoredBars = [],
  geometryRootRef,
  canEdit = false,
  onSaveTimingMap,
  focusOnIds = [],
  onEditModeChange = () => {},
}) {
  const completeBars = useMemo(() => authoredBars
    .filter((bar) => bar.revisionId === revisionId && bar.endCount - bar.startCount === 7)
    .sort((left, right) => left.startCount - right.startCount), [authoredBars, revisionId]);
  const initialOrder = useMemo(
    () => orderedDemonstrations(candidates).map(({ demonstrationId }) => demonstrationId),
    [candidates],
  );
  const [priorityOrder, setPriorityOrder] = useState(initialOrder);
  const [geometryVersion, setGeometryVersion] = useState(0);
  const [savingId, setSavingId] = useState('');
  const [notice, setNotice] = useState('');
  // MCRO001-F0028 amendment (owner report 2026-08-26): while a pointer/keyboard
  // gesture is retiming or shifting ONE candidate, this holds its live,
  // bar-snapped target so the rail can render it immediately — top/height
  // never wait on onSaveTimingMap's round-trip (which can legitimately take
  // several seconds under the F0058 429 backoff). It is cleared either once
  // `candidates` (server truth, via the parent's refetch) confirms the same
  // counts, or immediately on a failed save (revert-on-failure).
  const [dragPreview, setDragPreview] = useState(null);
  const pointerRef = useRef(null);
  // Owner ruling (2026-08-26): hovering/focusing/dragging a rail is "edit
  // mode" — rails may expand past the gutter (T-MCRO-028 Case D/E) and the
  // canvas's comment/annotation affordance must be unable to open for the
  // duration, folding back (and re-enabling comments) only once the pointer
  // truly leaves. `spreadActive` drives the expand CSS class for EVERYONE
  // (view-only actors already got the pre-existing hover/focus-within spread,
  // T-MCRO-028 — that stays); `onEditModeChange` (below) is the only channel
  // a sibling (CommunityReviewLayer) has into this state, and is gated to
  // canEdit since comment suppression only matters while editing is possible.
  //
  // This is driven by pointerenter/leave + focus/blur on the RAIL ROOT (a
  // fixed-size, never-repositioned box — see .mc-demonstration-timing-rail's
  // own width) rather than the native CSS `:hover`/`:focus-within` the
  // pre-existing per-line glow/tooltip rules still use. A real-browser
  // regression (choreography-demonstration-timing-rail.spec.ts) caught why
  // that matters: driving the LANE POSITION SHIFT itself off `:hover` on the
  // MOVING lines is self-undermining — expanding a lane can carry it out
  // from under a stationary cursor, dropping :hover, snapping it back under
  // the cursor, re-triggering hover, forever (Chromium re-hit-tests after
  // the layout change). Gating the shift by hovering the STATIC outer
  // container instead removes the feedback loop entirely: the trigger
  // region never moves, so it can't be un-hovered by its own effect.
  const [spreadActive, setSpreadActive] = useState(false);
  const editModeRef = useRef(false);
  const hoverRef = useRef(false);
  const focusRef = useRef(false);
  const dragActiveRef = useRef(false);
  const foldTimeoutRef = useRef(null);
  useEffect(() => setPriorityOrder(initialOrder), [initialOrder]);
  useLayoutEffect(() => {
    const root = geometryRootRef?.current;
    if (!root) return undefined;
    const update = () => setGeometryVersion((current) => current + 1);
    update();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    observer?.observe(root);
    globalThis.addEventListener?.('resize', update);
    return () => {
      observer?.disconnect();
      globalThis.removeEventListener?.('resize', update);
    };
  }, [geometryRootRef]);
  useEffect(() => () => {
    if (foldTimeoutRef.current) clearTimeout(foldTimeoutRef.current);
    // Safety net: an unmount mid-drag/hover (e.g. navigating away) must not
    // leave text selection permanently disabled, or comments permanently
    // suppressed, for the rest of the canvas.
    globalThis.document?.body?.classList?.remove('mc-rail-dragging');
    if (globalThis.document?.body) globalThis.document.body.dataset.mcRailEditModeActive = '';
  }, []);

  const setEditMode = (active) => {
    if (editModeRef.current === active) return;
    editModeRef.current = active;
    setSpreadActive(active);
    if (canEdit) {
      // MCRO001-F0060/T-MCRO-060 (owner ruling 2026-08-26, confirmed by an
      // independent real-browser check): the `onEditModeChange` prop callback
      // below is React-state-propagated — DemonstrationTimingRail sets state,
      // RoutineCanvasEditor re-renders, CommunityReviewLayer re-renders,
      // ITS OWN effect updates its ref — at least two render cycles away. A
      // native selection event on the canvas textarea can fire synchronously,
      // before that chain finishes flushing, especially right at drag start.
      // This DOM attribute is the same signal set with ZERO render latency —
      // read directly (not through React) by CommunityReview.js — closing
      // that specific timing window; the prop stays the primary, tested,
      // React-idiomatic contract for everything not in that race.
      if (globalThis.document?.body) {
        globalThis.document.body.dataset.mcRailEditModeActive = active ? '1' : '';
      }
      onEditModeChange(active);
    }
  };
  const enterEditMode = () => {
    if (foldTimeoutRef.current) { clearTimeout(foldTimeoutRef.current); foldTimeoutRef.current = null; }
    setEditMode(true);
  };
  const scheduleFoldBack = () => {
    if (foldTimeoutRef.current) clearTimeout(foldTimeoutRef.current);
    foldTimeoutRef.current = setTimeout(() => {
      foldTimeoutRef.current = null;
      if (hoverRef.current || focusRef.current || dragActiveRef.current) return;
      setEditMode(false);
    }, RAIL_EDIT_MODE_FOLD_GRACE_MS);
  };

  const locallyOrdered = useMemo(
    () => applyPriorityOrder(candidates, priorityOrder),
    [candidates, priorityOrder],
  );
  const lines = useMemo(() => demonstrationLanes(locallyOrdered), [locallyOrdered]);
  // T-MCRO-085: served records whose timing map failed hard validation arrive
  // here (never through `candidates`, whose projection math requires a valid
  // map) and render as honest error lines — present, labelled, non-interactive.
  const errorLines = useMemo(() => orderedDemonstrations(errorCandidates)
    .map((candidate, index) => ({ ...candidate, lane: index })), [errorCandidates]);
  // MCRO001-F0052: the rail colours each line by the video's shared media-keyed
  // track colour, so the line for a video is the same colour as that video's Video
  // Library tile border. Keyed by mediaId (else demonstrationId) — see mediaTrackColour.js.
  const colours = useMemo(
    () => mediaTrackColours([...locallyOrdered, ...errorLines]),
    [locallyOrdered, errorLines],
  );
  const focusSet = useMemo(() => new Set(focusOnIds), [focusOnIds]);
  const context = { sections, authoredBars: completeBars, revisionId };

  // Clears the optimistic preview once the parent's own state (recomputed
  // from a completed refetch) agrees with what was already being shown —
  // never earlier (that would flicker back to stale server geometry mid- or
  // post-save) and never later (the preview would otherwise linger forever).
  useEffect(() => {
    if (!dragPreview) return;
    const settled = locallyOrdered.find((item) => item.demonstrationId === dragPreview.demonstrationId);
    if (settled && settled.timingMap.startCount === dragPreview.startCount
        && settled.timingMap.endCount === dragPreview.endCount) {
      setDragPreview(null);
    }
  }, [locallyOrdered, dragPreview]);

  const commit = async (candidate, timingMap, nextOrder = priorityOrder) => {
    if (!canEdit || candidate.canEditTiming !== true
        || !Number.isInteger(candidate.expectedMapVersion) || candidate.expectedMapVersion < 0
        || typeof onSaveTimingMap !== 'function' || savingId) return false;
    setSavingId(candidate.demonstrationId);
    setNotice('');
    try {
      const result = await onSaveTimingMap(timingMapMutationRequest(candidate, timingMap, nextOrder));
      if (result?.committed !== true) throw new Error('not committed');
      setPriorityOrder(nextOrder);
      setNotice('Demonstration timing and priority saved.');
      return true;
    } catch (_) {
      setNotice('The demonstration timing could not be saved. No timing or priority change was committed.');
      return false;
    } finally {
      setSavingId('');
    }
  };

  const moveEndpoint = (candidate, endpoint, bar) => {
    try {
      return commit(candidate, moveTimingMapEndpoint(candidate.timingMap, endpoint, bar, context));
    } catch (_) {
      setNotice('Choose an ordered range of complete 8-beat bars in this revision. No change was committed.');
      return false;
    }
  };
  const shift = (candidate, delta) => {
    try {
      return commit(candidate, shiftTimingMapWindow(candidate.timingMap, delta, context));
    } catch (_) {
      setNotice('The demonstration cannot move beyond this revision’s complete 8-beat bars. No change was committed.');
      return false;
    }
  };
  const reorder = (candidate, direction) => {
    const next = reorderPriority(priorityOrder, candidate.demonstrationId, direction);
    if (next.join('') === priorityOrder.join('')) return false;
    const previousOrder = priorityOrder;
    // Optimistic: reordering is a priority swap, not a timing retime, so
    // there is no bar-snapped position to preview — but the lane reshuffle
    // itself need not wait on the save round-trip either.
    setPriorityOrder(next);
    return commit(candidate, candidate.timingMap, next).then((committed) => {
      if (!committed) setPriorityOrder(previousOrder);
      return committed;
    });
  };

  // Applies a bar-snapped endpoint/window target the instant a gesture (drag
  // or keyboard nudge) determines it, then fires the real save in the
  // background — the rail never waits on the network to show where the
  // gesture landed. `revert()` restores server truth only if the save
  // ultimately fails; a successful save is reconciled by the effect above.
  const previewThenSave = (candidateId, mode, nextCounts, save) => {
    if (nextCounts) setDragPreview({ demonstrationId: candidateId, mode, ...nextCounts });
    Promise.resolve(save()).then((committed) => {
      if (committed === false) setDragPreview(null);
    });
  };

  // MCRO001-F0060/T-MCRO-060 (owner report 2026-08-26): even while pointer
  // capture correctly redirects every pointerdown/move/up event to THIS
  // button (confirmed instrumented in a real browser — event.target stayed
  // this button throughout), Chromium's NATIVE drag-select gesture recogniser
  // runs independently of the JS event-capture/dispatch system: a hover-
  // expanded outer lane's drag path routinely crosses real text-column
  // pixels (a high lane index is deliberately allowed past the gutter on
  // hover/drag — requirement 2), and the browser forms a genuine textarea
  // selection there regardless of which element the pointer events were
  // dispatched to. That selection is what a naive `updateSelection` would
  // then read as "the user selected this" on release. The fix is not a
  // suppression-timing race against that selection forming — it's to STOP
  // the selection from forming at all: `document.body` gets a
  // `mc-rail-dragging` class for the exact duration of the drag (see
  // pointerDown below), and MusicChoreography.css sets `user-select: none`
  // under it. `finishDrag` is the single finaliser both the button's own
  // onPointerUp AND a document-level pointerup/pointercancel failsafe call
  // (pointer capture can still separately lapse for OTHER reasons — an
  // already-cancelled touch contact, a window blur — so this failsafe stays
  // as defence-in-depth), guarded so exactly one of them does the work
  // (pointerRef.current is nulled on first entry).
  const finishDrag = (event) => {
    const drag = pointerRef.current;
    if (!drag) return; // already finalised via the other path
    pointerRef.current = null;
    dragActiveRef.current = false;
    globalThis.document?.body?.classList?.remove('mc-rail-dragging');
    scheduleFoldBack();
    const endBar = barAtY(geometryRootRef?.current, completeBars, event.clientY);
    if (drag.mode === 'start' || drag.mode === 'end') {
      Promise.resolve(moveEndpoint(drag.candidate, drag.mode, endBar)).then((committed) => {
        if (committed === false) setDragPreview(null);
      });
      return;
    }
    const horizontal = event.clientX - drag.startX;
    const startIndex = completeBars.indexOf(drag.startBar);
    const endIndex = completeBars.indexOf(endBar);
    if (Math.abs(horizontal) >= 18) {
      // This gesture reprioritises rather than retiming the window — release
      // the vertical preview back to (already-unchanged) server geometry.
      setDragPreview(null);
      reorder(drag.candidate, horizontal > 0 ? 1 : -1);
    } else if (startIndex >= 0 && endIndex >= 0 && startIndex !== endIndex) {
      Promise.resolve(shift(drag.candidate, endIndex - startIndex)).then((committed) => {
        if (committed === false) setDragPreview(null);
      });
    }
  };
  const pointerDown = (event, candidate, mode) => {
    if (!canEdit || candidate.canEditTiming !== true || savingId) return;
    event.preventDefault();
    // Owner ruling (2026-08-26): a rail gesture must never leak to the canvas
    // line underneath (it was reopening the comment box mid-drag). Event
    // containment (stopPropagation) is defence-in-depth; the actual fix is
    // the body class added below, which stops the browser's native
    // drag-select gesture from ever forming on the textarea underneath in
    // the first place — see finishDrag's own comment for why that, not an
    // event-propagation leak, was the real mechanism.
    event.stopPropagation();
    // Pointer capture can be rejected for an already-cancelled touch contact
    // (or simply not hold for the whole gesture — see finishDrag's own
    // comment). The document-level failsafe below, not this, is what
    // actually guarantees drag-end cleanup runs.
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) { /* continue without capture */ }
    pointerRef.current = {
      candidate,
      mode,
      startX: event.clientX,
      startBar: barAtY(geometryRootRef?.current, completeBars, event.clientY),
    };
    dragActiveRef.current = true;
    // MCRO001-F0060/T-MCRO-060: see finishDrag's comment — this is what
    // actually prevents a native text selection from forming on the canvas
    // textarea underneath while a hover-expanded, off-gutter drag is live.
    globalThis.document?.body?.classList?.add('mc-rail-dragging');
    enterEditMode();
    setDragPreview({
      demonstrationId: candidate.demonstrationId,
      mode,
      startCount: candidate.timingMap.startCount,
      endCount: candidate.timingMap.endCount,
    });
    const documentFailsafe = (finishEvent) => {
      globalThis.document?.removeEventListener?.('pointerup', documentFailsafe);
      globalThis.document?.removeEventListener?.('pointercancel', documentFailsafe);
      finishDrag(finishEvent);
    };
    globalThis.document?.addEventListener?.('pointerup', documentFailsafe);
    globalThis.document?.addEventListener?.('pointercancel', documentFailsafe);
  };
  const pointerMove = (event) => {
    const drag = pointerRef.current;
    if (!drag) return;
    const root = geometryRootRef?.current;
    if (drag.mode === 'body') {
      const currentBar = barAtY(root, completeBars, event.clientY);
      const startIndex = completeBars.indexOf(drag.startBar);
      const currentIndex = completeBars.indexOf(currentBar);
      if (startIndex < 0 || currentIndex < 0) return;
      const preview = previewShiftedWindow(drag.candidate.timingMap, currentIndex - startIndex, context);
      if (preview) setDragPreview({ demonstrationId: drag.candidate.demonstrationId, mode: 'body', ...preview });
      return;
    }
    const targetBar = barAtY(root, completeBars, event.clientY);
    if (!targetBar) return;
    setDragPreview((previous) => {
      const base = previous && previous.demonstrationId === drag.candidate.demonstrationId
        ? previous
        : { startCount: drag.candidate.timingMap.startCount, endCount: drag.candidate.timingMap.endCount };
      const startCount = drag.mode === 'start' ? targetBar.startCount : base.startCount;
      const endCount = drag.mode === 'end' ? targetBar.endCount : base.endCount;
      // An inverted drag (start dragged past end, or vice versa) keeps the
      // last valid preview rather than rendering a negative-height line.
      if (startCount > endCount) return previous;
      return { demonstrationId: drag.candidate.demonstrationId, mode: drag.mode, startCount, endCount };
    });
  };
  const pointerUp = (event) => {
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch (_) { /* capture was unavailable */ }
    event.stopPropagation();
    finishDrag(event);
  };

  // T-MCRO-085: within a rendering rail (non-empty complete-bar set — the
  // spec's own scope boundary) a served map's line is never silently absent:
  // empty `lines` with only error records still renders those error lines.
  if ((lines.length === 0 && errorLines.length === 0) || completeBars.length === 0) return null;
  // geometryVersion intentionally invalidates these DOM-derived positions after resize/reflow.
  void geometryVersion;
  const laneCount = lines.reduce((max, line) => Math.max(max, Number(line.lane) || 0), 0) + 1;
  // MCRO001-F0028 amendment (owner ruling, screenshot-driven, 2026-08-26,
  // amended 2026-09-07): the rail's whole geometry is anchored to the
  // CANVAS'S OWN real free margin — never a hardcoded fallback. That margin
  // is NOT simply `.mc-continuous-canvas-mirror`'s computed padding-left:
  // the 2026-09-07 ruling established that the bar-count chip, spanning-
  // bracket duration label and section title are themselves content a rail
  // must never touch, even though MusicChoreography.css deliberately
  // renders them pulled back INSIDE that padding band. measureCanvasGutterPx
  // measures whichever boundary is real — the annotation's left edge when
  // one is rendered, the padding edge otherwise. See
  // demonstrationTimingRailGeometry.js for the containment construction
  // this guarantees regardless of viewport, lane count or which boundary
  // measureCanvasGutterPx resolves to.
  const gutterPx = measureCanvasGutterPx(geometryRootRef?.current);
  const geometryStyle = railGeometryStyle(gutterPx, laneCount);
  // Unconditional (not canEdit-gated): the pre-existing spread-on-hover is a
  // legibility aid for EVERY actor, editor or viewer (T-MCRO-028) — only the
  // onward `onEditModeChange` notification inside setEditMode is canEdit-gated.
  //
  // Attached to BOTH the rail root AND every individual line below (not
  // root-only): an independent real-browser check (codex-delegate,
  // 2026-08-26) caught that root-only tracking has a gap the earlier
  // comment above didn't anticipate — the root's own box is a fixed 8rem,
  // but a highly-expanded OUTER lane (high `--mc-demo-lane` index; hover
  // pitch grows with lane index) can render its actual interactive elements
  // well past that fixed width once spread. The cursor then exits the
  // ROOT's box (mid-hover, mid-drag) even though it is still squarely over
  // a real rail control, edit mode folds back on schedule, and a
  // still-in-flight drag's eventual native mouseup — landing on the canvas
  // textarea if pointer capture didn't hold for the whole gesture — reopens
  // the comment/structural toolbar. Each LINE's own bounding box always
  // matches wherever it is ACTUALLY rendered, rest or expanded, so tracking
  // it directly has no such gap; the root attachment remains as cheap cover
  // for the gutter margin between lines.
  const spreadHandlers = {
    onPointerEnter: () => { hoverRef.current = true; enterEditMode(); },
    onPointerLeave: () => { hoverRef.current = false; scheduleFoldBack(); },
    onFocus: () => { focusRef.current = true; enterEditMode(); },
    onBlur: () => { focusRef.current = false; scheduleFoldBack(); },
  };
  return <div
    className={`mc-demonstration-timing-rail${spreadActive ? ' edit-active' : ''}`}
    role="region"
    aria-label="Demonstration timing rail"
    style={geometryStyle}
    {...spreadHandlers}
  >
    <div className="mc-demonstration-timing-lines">
      {lines.map((candidate) => {
        const editable = canEdit && candidate.canEditTiming === true
          && Number.isInteger(candidate.expectedMapVersion) && candidate.expectedMapVersion >= 0
          && typeof onSaveTimingMap === 'function';
        const preview = dragPreview && dragPreview.demonstrationId === candidate.demonstrationId ? dragPreview : null;
        const effectiveStart = preview ? preview.startCount : candidate.timingMap.startCount;
        const effectiveEnd = preview ? preview.endCount : candidate.timingMap.endCount;
        const startIndex = barIndexAtCount(completeBars, effectiveStart);
        const endIndex = barIndexAtCount(completeBars, effectiveEnd, 'end');
        // T-MCRO-085 case B: the pre-F0085 code returned null here when no
        // exact complete-bar boundary anchor resolved — the second measured
        // silent-drop path behind #39. The line now always renders: yForCount
        // clamps unresolved counts to the nearest containing bar band (or the
        // rail's top edge), and the accessible name states honestly that the
        // bars cannot be resolved instead of fabricating bar numbers.
        const anchorResolved = startIndex >= 0 && endIndex >= startIndex;
        // T-MCRO-085 case C: where anchors are genuinely missing, retime and
        // shift stay unavailable (no fake targets); the line states why.
        // Media-degraded lines (case A) are anchorable and edit exactly as
        // T-MCRO-060 specifies — degradation is about media, not the map.
        const editableLine = editable && anchorResolved;
        const top = yForCount(geometryRootRef?.current, completeBars, effectiveStart);
        const bottom = yForCount(geometryRootRef?.current, completeBars, effectiveEnd, true);
        const focusOn = focusSet.has(candidate.demonstrationId);
        const degraded = candidate.degraded;
        const mediaClause = degraded === 'media-loading'
          ? 'map present — media loading, showing counts from the saved map'
          : degraded === 'media-unavailable'
            ? 'map present — media unavailable, showing counts from the saved map'
            : '';
        const positionClause = anchorResolved
          ? `bars ${startIndex + 1} through ${endIndex + 1}`
          : `bars cannot be resolved on this revision (saved counts ${effectiveStart}–${effectiveEnd})`;
        const label = `${candidate.sequenceLabel}, ${positionClause}, priority ${candidate.priority + 1}${focusOn ? ', focus-on' : ''}${mediaClause ? ` — ${mediaClause}` : ''}`;
        // Discoverability (owner-reported): the colour line alone carried no
        // sighted-visible identity — only this same text reached a screen
        // reader, via the group aria-label below. Editors additionally get it
        // as a visible chip on the existing hover/focus-within trigger, plus a
        // wider hit target and an always-on halo (mc-demonstration-timing-tooltip
        // / .editable rules, MusicChoreography.css) so the line reads as a
        // grabbable control at rest, not decoration. View-only actors get
        // neither — the rail is visually unchanged for them.
        // Editor chip (aria-hidden decoration): keeps its compact historical
        // `bars 1–2` form for resolved lines; degraded lines append the same
        // honest media statement the accessible name carries.
        const tooltip = `${candidate.sequenceLabel} · ${anchorResolved ? `bars ${startIndex + 1}–${endIndex + 1}` : positionClause}${mediaClause ? ` · ${mediaClause}` : ''}`;
        return <div
          className={`mc-demonstration-timing-line${editableLine ? ' editable' : ''}${focusOn ? ' focus-on' : ''}${savingId === candidate.demonstrationId ? ' saving' : ''}${degraded ? ` degraded ${degraded}` : ''}${anchorResolved ? '' : ' anchor-unresolved'}`}
          style={{
            '--mc-demo-colour': colours[mediaTrackKey(candidate)],
            '--mc-demo-lane': candidate.lane,
            top: `${top}px`,
            height: `${Math.max(8, bottom - top)}px`,
          }}
          role="group"
          aria-label={label}
          key={candidate.demonstrationId}
          {...spreadHandlers}
        >
          <button
            type="button"
            className="mc-demonstration-endpoint start"
            aria-label={`Move start of ${label}`}
            disabled={!editableLine || Boolean(savingId)}
            onPointerDown={(event) => pointerDown(event, candidate, 'start')}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onKeyDown={(event) => {
              if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              const target = completeBars[startIndex + (event.key === 'ArrowDown' ? 1 : -1)];
              if (!target) return;
              previewThenSave(candidate.demonstrationId, 'start', { startCount: target.startCount, endCount: effectiveEnd },
                () => moveEndpoint(candidate, 'start', target));
            }}
          />
          <button
            type="button"
            className="mc-demonstration-line-body"
            aria-label={`Move or reorder ${label}`}
            disabled={!editableLine || Boolean(savingId)}
            onPointerDown={(event) => pointerDown(event, candidate, 'body')}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onKeyDown={(event) => {
              if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
                const delta = event.key === 'ArrowDown' ? 1 : -1;
                const preview2 = previewShiftedWindow(candidate.timingMap, delta, context);
                previewThenSave(candidate.demonstrationId, 'body', preview2, () => shift(candidate, delta));
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
                reorder(candidate, event.key === 'ArrowRight' ? 1 : -1);
              }
            }}
          />
          <button
            type="button"
            className="mc-demonstration-endpoint end"
            aria-label={`Move end of ${label}`}
            disabled={!editableLine || Boolean(savingId)}
            onPointerDown={(event) => pointerDown(event, candidate, 'end')}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onKeyDown={(event) => {
              if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              const target = completeBars[endIndex + (event.key === 'ArrowDown' ? 1 : -1)];
              if (!target) return;
              previewThenSave(candidate.demonstrationId, 'end', { startCount: effectiveStart, endCount: target.endCount },
                () => moveEndpoint(candidate, 'end', target));
            }}
          />
          {focusOn && <span className="mc-visually-hidden">Focus-on detailed breakdown</span>}
          {editableLine && <span className="mc-demonstration-timing-tooltip" aria-hidden="true">{tooltip}</span>}
        </div>;
      })}
      {errorLines.map((candidate) => (
        <div
          className="mc-demonstration-timing-line error invalid-map"
          style={{
            '--mc-demo-colour': colours[mediaTrackKey(candidate)],
            '--mc-demo-lane': candidate.lane,
            top: '0px',
            height: '8px',
          }}
          role="group"
          aria-label={`${candidate.sequenceLabel}: saved map failed validation — timing cannot be shown`}
          key={candidate.demonstrationId}
          {...spreadHandlers}
        />
      ))}
    </div>
    {notice && <div className="mc-visually-hidden" role="status">{notice}</div>}
  </div>;
}
